"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type RefCallback,
} from "react";
import { useNavigationLoading } from "@/components/shell/navigation-loading-context";
import {
  assertBoundedToolLoadMetricFields,
  classifyLiveToolPath,
  TOOL_LOAD_CLIENT_OBSERVABILITY_ENABLED,
  classifyPrimarySample,
  TOOL_LOAD_RUNNER_SESSION_KEY,
  type ActiveToolLoadTrace,
  type FinishedToolLoadTrace,
  type LiveToolSurface,
  type ToolContentReceipt,
} from "@/lib/performance/tool-load-timing";
import { awaitsFirstLiveRow, reportFirstLiveRow } from "@/lib/performance/first-live-row";
import { phCapture } from "@/utils/analytics";

type PaintMethod = "element-timing" | "animation-frame";
type RegisteredContent = {
  element: HTMLElement;
  receipt: ToolContentReceipt;
  committedAtMs: number;
  hasRealRow: boolean;
};
type PaintCandidate = { contentId: string; method: PaintMethod; paintedAtMs: number };
type RootHydrationCommitCandidate = {
  surface: LiveToolSurface;
  pathname: string;
  committedAtMs: number;
};
type FreshValidationSource = "client-200" | "client-304" | "delta-valid" | "semantic-empty";
type FreshValidationCandidate = {
  surface: LiveToolSurface;
  source: FreshValidationSource;
  completedAtMs: number;
};

const PAINT_CANDIDATE_EVENT = "upside:tool-content-paint";
export const TOOL_ROOT_HYDRATION_COMMIT_EVENT = "upside:tool-root-hydration-commit";
const FRESH_VALIDATION_EVENT = "upside:tool-fresh-validation";
export const TOOL_LOAD_RUNNER_EVENT = "upside:tool-load-runner";
export { TOOL_LOAD_RUNNER_SESSION_KEY } from "@/lib/performance/tool-load-timing";
const registeredContent = TOOL_LOAD_CLIENT_OBSERVABILITY_ENABLED
  ? new Map<string, RegisteredContent>()
  : null;

export type ToolContentMarkerProps<T extends HTMLElement = HTMLDivElement> = {
  ref: RefCallback<T>;
  "data-tool-content-ready"?: "true";
  "data-tool-content-trustworthy"?: "true";
  "data-tool-content-has-real-row"?: "true";
  "data-tool-content-surface"?: LiveToolSurface;
  "data-tool-content-id"?: string;
  elementtiming?: string;
};

function dispatchPaintCandidate(candidate: PaintCandidate): void {
  window.dispatchEvent(new CustomEvent<PaintCandidate>(PAINT_CANDIDATE_EVENT, { detail: candidate }));
}

function afterPaint(callback: () => void): () => void {
  let cancelled = false;
  let secondFrame = 0;
  const firstFrame = window.requestAnimationFrame(() => {
    secondFrame = window.requestAnimationFrame(() => {
      if (!cancelled) callback();
    });
  });
  return () => {
    cancelled = true;
    window.cancelAnimationFrame(firstFrame);
    if (secondFrame) window.cancelAnimationFrame(secondFrame);
  };
}

function runnerEnabled(): boolean {
  if (!TOOL_LOAD_CLIENT_OBSERVABILITY_ENABLED) return false;
  try {
    return window.sessionStorage.getItem(TOOL_LOAD_RUNNER_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

function dispatchRunnerEvent(detail: Readonly<Record<string, unknown>>): void {
  if (!runnerEnabled()) return;
  window.dispatchEvent(new CustomEvent(TOOL_LOAD_RUNNER_EVENT, { detail }));
}

function captureOrganicToolLoad(
  event: "tool_load_milestone" | "tool_load_terminal",
  properties: Readonly<Record<string, unknown>>,
): void {
  if (!TOOL_LOAD_CLIENT_OBSERVABILITY_ENABLED) return;
  if (!runnerEnabled()) phCapture(event, properties);
}

export function reportToolFreshValidation(
  surface: LiveToolSurface,
  source: FreshValidationSource,
  completedAtMs = performance.now(),
): void {
  if (!TOOL_LOAD_CLIENT_OBSERVABILITY_ENABLED) return;
  if (
    !["client-200", "client-304", "delta-valid", "semantic-empty"].includes(source) ||
    !Number.isFinite(completedAtMs)
  ) return;
  window.dispatchEvent(new CustomEvent<FreshValidationCandidate>(FRESH_VALIDATION_EVENT, {
    detail: { surface, source, completedAtMs },
  }));
}

export function useOwnsActiveToolLoad(surface: LiveToolSurface): () => boolean {
  const { toolLoadTrace } = useNavigationLoading();
  const traceRef = useRef(toolLoadTrace);
  traceRef.current = toolLoadTrace;
  return useCallback(() => {
    if (!TOOL_LOAD_CLIENT_OBSERVABILITY_ENABLED) return false;
    const trace = traceRef.current;
    return trace?.state === "active" && trace.targetSurface === surface;
  }, [surface]);
}

export function useToolRootHydration(surface: LiveToolSurface): void {
  useLayoutEffect(() => {
    if (!TOOL_LOAD_CLIENT_OBSERVABILITY_ENABLED) return;
    const route = classifyLiveToolPath(window.location.pathname);
    if (route?.surface !== surface) return;
    window.dispatchEvent(new CustomEvent<RootHydrationCommitCandidate>(
      TOOL_ROOT_HYDRATION_COMMIT_EVENT,
      {
        detail: {
          surface,
          pathname: route.pathname,
          committedAtMs: performance.now(),
        },
      },
    ));
  }, [surface]);
}

function isVisibleContent(element: HTMLElement): boolean {
  if (!element.isConnected || element.getClientRects().length === 0) return false;
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  if (
    rect.bottom <= 0 ||
    rect.right <= 0 ||
    rect.top >= window.innerHeight ||
    rect.left >= window.innerWidth
  ) return false;
  let current: HTMLElement | null = element;
  while (current) {
    const style = window.getComputedStyle(current);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      Number.parseFloat(style.opacity || "1") <= 0
    ) return false;
    current = current.parentElement;
  }
  return true;
}

export function useToolContentBoundary<T extends HTMLElement = HTMLDivElement>(
  receipt: ToolContentReceipt | null | undefined,
  options: { hasRealRow: boolean } = { hasRealRow: false },
): ToolContentMarkerProps<T> {
  const { toolLoadTrace } = useNavigationLoading();
  const observesActiveTrace = Boolean(
    TOOL_LOAD_CLIENT_OBSERVABILITY_ENABLED &&
    receipt &&
    toolLoadTrace?.state === "active" &&
    toolLoadTrace.targetSurface === receipt.surface,
  );
  const elementRef = useRef<T | null>(null);
  const setRef = useCallback<RefCallback<T>>((element) => {
    elementRef.current = element;
  }, []);

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (
      !receipt ||
      !element ||
      !observesActiveTrace
    ) return;

    const committedAtMs = performance.now();
    const registration = { element, receipt, committedAtMs, hasRealRow: options.hasRealRow };
    registeredContent?.set(receipt.contentId, registration);
    const cancelPaint = afterPaint(() => dispatchPaintCandidate({
      contentId: receipt.contentId,
      method: "animation-frame",
      paintedAtMs: performance.now(),
    }));

    return () => {
      cancelPaint();
      if (registeredContent?.get(receipt.contentId) === registration) {
        registeredContent.delete(receipt.contentId);
      }
    };
  }, [observesActiveTrace, options.hasRealRow, receipt, toolLoadTrace?.traceId]);

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!receipt || !element || !(options.hasRealRow || receipt.source === "semantic-empty") || !awaitsFirstLiveRow(receipt.surface)) return;
    const board = options.hasRealRow ? "rows" : "empty";
    return afterPaint(() => {
      if (isVisibleContent(element)) reportFirstLiveRow(receipt.surface, performance.now(), board);
    });
  }, [options.hasRealRow, receipt]);

  return {
    ref: setRef,
    "data-tool-content-ready": receipt ? "true" : undefined,
    "data-tool-content-trustworthy": receipt?.trustworthy ? "true" : undefined,
    "data-tool-content-has-real-row": receipt && options.hasRealRow ? "true" : undefined,
    "data-tool-content-surface": receipt?.surface,
    "data-tool-content-id": observesActiveTrace ? receipt?.contentId : undefined,
    elementtiming: observesActiveTrace ? receipt?.contentId : undefined,
  };
}

function viewportClass(): "mobile" | "desktop" {
  return window.matchMedia("(max-width: 767px)").matches ? "mobile" : "desktop";
}

function wasRedirected(trace: {
  initialTargetPath: string;
  committedPaths: readonly string[];
}): boolean {
  return (
    trace.committedPaths.length > 1 ||
    (trace.committedPaths.length === 1 && trace.committedPaths[0] !== trace.initialTargetPath)
  );
}

export function ToolLoadObserver() {
  if (!TOOL_LOAD_CLIENT_OBSERVABILITY_ENABLED) return null;
  return <EnabledToolLoadObserver />;
}

function EnabledToolLoadObserver() {
  const { toolLoadTrace, completeToolLoad } = useNavigationLoading();
  const traceRef = useRef(toolLoadTrace);
  const recordedMilestones = useRef(new Set<string>());
  const recordedTraceId = useRef<string | null>(null);
  const visibilityCompromised = useRef(false);
  const pendingFreshValidation = useRef<{
    traceId: string;
    candidate: FreshValidationCandidate;
  } | null>(null);
  const pendingRootCommits = useRef<{
    traceId: string;
    candidates: Map<string, RootHydrationCommitCandidate>;
  } | null>(null);
  traceRef.current = toolLoadTrace;
  const currentTraceId = toolLoadTrace?.traceId ?? null;
  if (currentTraceId !== recordedTraceId.current) {
    recordedMilestones.current.clear();
    recordedTraceId.current = currentTraceId;
    visibilityCompromised.current = document.visibilityState !== "visible";
    if (pendingFreshValidation.current?.traceId !== currentTraceId) {
      pendingFreshValidation.current = null;
    }
    if (pendingRootCommits.current?.traceId !== currentTraceId) {
      pendingRootCommits.current = null;
    }
  }

  const recordFreshValidation = useCallback((
    trace: FinishedToolLoadTrace,
    candidate: FreshValidationCandidate,
  ) => {
    if (
      candidate.surface !== trace.targetSurface ||
      (trace.terminal.contentSource !== "snapshot-fresh" &&
        trace.terminal.contentSource !== "degraded-usable")
    ) return;
    const key = `${trace.traceId}:first_fresh_validation`;
    if (recordedMilestones.current.has(key)) return;
    recordedMilestones.current.add(key);
    const properties = {
      milestone: "first_fresh_validation",
      surface: candidate.surface,
      content_source: candidate.source,
      duration_ms: Math.max(0, candidate.completedAtMs - trace.startedAtMs),
      commit_count: trace.committedPaths.length,
    };
    assertBoundedToolLoadMetricFields(properties);
    captureOrganicToolLoad("tool_load_milestone", properties);
    dispatchRunnerEvent({
      kind: "milestone",
      surface: candidate.surface,
      milestone: "first_fresh_validation",
      contentSource: candidate.source,
      durationMs: properties.duration_ms,
      commitCount: properties.commit_count,
    });
  }, []);

  const recordRootHydrationCommit = useCallback((
    trace: ActiveToolLoadTrace,
    candidate: RootHydrationCommitCandidate,
  ) => {
    if (
      candidate?.surface !== trace.targetSurface ||
      classifyLiveToolPath(candidate.pathname)?.surface !== trace.targetSurface ||
      !Number.isFinite(candidate?.committedAtMs) ||
      candidate.committedAtMs < trace.startedAtMs ||
      trace.committedPaths.at(-1) !== candidate.pathname
    ) return;
    const pathCommit = trace.committedPathEntries.at(-1);
    if (!pathCommit || pathCommit.pathname !== candidate.pathname) return;
    const routeDurationMs = Math.max(
      0,
      Math.min(pathCommit.atMs, candidate.committedAtMs) - trace.startedAtMs,
    );
    const hydrationDurationMs = Math.max(0, candidate.committedAtMs - trace.startedAtMs);
    const commitCount = trace.committedPaths.length;
    const routeKey = `${trace.traceId}:route_content_commit`;
    if (!recordedMilestones.current.has(routeKey)) {
      recordedMilestones.current.add(routeKey);
      const properties = {
        milestone: "route_content_commit",
        surface: trace.targetSurface,
        duration_ms: routeDurationMs,
        commit_count: commitCount,
      };
      assertBoundedToolLoadMetricFields(properties);
      captureOrganicToolLoad("tool_load_milestone", properties);
      dispatchRunnerEvent({
        kind: "milestone",
        surface: trace.targetSurface,
        milestone: "route_content_commit",
        durationMs: routeDurationMs,
        commitCount,
        routeChain: [...trace.committedPaths],
      });
    }
    const hydrationKey = `${trace.traceId}:hydration_commit`;
    if (!recordedMilestones.current.has(hydrationKey)) {
      recordedMilestones.current.add(hydrationKey);
      dispatchRunnerEvent({
        kind: "milestone",
        surface: candidate.surface,
        milestone: "hydration_commit",
        durationMs: hydrationDurationMs,
        commitCount,
      });
    }
    if (pendingRootCommits.current?.traceId === trace.traceId) {
      pendingRootCommits.current.candidates.delete(candidate.pathname);
    }
  }, []);

  useLayoutEffect(() => {
    const onRootHydrationCommit = (event: Event) => {
      const candidate = (event as CustomEvent<RootHydrationCommitCandidate>).detail;
      const trace = traceRef.current;
      if (
        trace?.state !== "active" ||
        candidate?.surface !== trace.targetSurface ||
        classifyLiveToolPath(candidate?.pathname)?.surface !== trace.targetSurface ||
        !Number.isFinite(candidate?.committedAtMs) ||
        candidate.committedAtMs < trace.startedAtMs
      ) return;
      let pending = pendingRootCommits.current;
      if (!pending || pending.traceId !== trace.traceId) {
        pending = { traceId: trace.traceId, candidates: new Map() };
        pendingRootCommits.current = pending;
      }
      const previous = pending.candidates.get(candidate.pathname);
      const selectedCandidate = !previous || candidate.committedAtMs < previous.committedAtMs
        ? candidate
        : previous;
      pending.candidates.set(candidate.pathname, selectedCandidate);
      recordRootHydrationCommit(trace, selectedCandidate);
    };
    window.addEventListener(TOOL_ROOT_HYDRATION_COMMIT_EVENT, onRootHydrationCommit);
    return () => window.removeEventListener(
      TOOL_ROOT_HYDRATION_COMMIT_EVENT,
      onRootHydrationCommit,
    );
  }, [recordRootHydrationCommit]);

  useLayoutEffect(() => {
    if (toolLoadTrace?.state !== "active" || toolLoadTrace.committedPaths.length === 0) return;
    const pending = pendingRootCommits.current;
    if (!pending || pending.traceId !== toolLoadTrace.traceId) return;
    const pathname = toolLoadTrace.committedPaths.at(-1);
    if (!pathname) return;
    const candidate = pending.candidates.get(pathname);
    if (candidate) recordRootHydrationCommit(toolLoadTrace, candidate);
  }, [
    recordRootHydrationCommit,
    toolLoadTrace?.state,
    toolLoadTrace?.traceId,
    toolLoadTrace?.committedPaths.length,
    toolLoadTrace?.committedPathEntries.length,
  ]);

  useEffect(() => {
    if (toolLoadTrace?.state !== "active") return;
    const recordVisibility = () => {
      if (document.visibilityState !== "visible") visibilityCompromised.current = true;
    };
    recordVisibility();
    document.addEventListener("visibilitychange", recordVisibility);
    return () => document.removeEventListener("visibilitychange", recordVisibility);
  }, [toolLoadTrace?.state, toolLoadTrace?.traceId]);

  const recordLoadingCommit = useCallback((element: Element) => {
    const trace = traceRef.current;
    if (trace?.state !== "active") return;
    const surface = element.getAttribute("data-tool-loading-surface") as LiveToolSurface | null;
    if (surface !== trace.targetSurface) return;
    const key = `${trace.traceId}:route_loading_commit`;
    if (recordedMilestones.current.has(key)) return;
    recordedMilestones.current.add(key);
    const properties = {
      milestone: "route_loading_commit",
      surface,
      duration_ms: Math.max(0, performance.now() - trace.startedAtMs),
      commit_count: trace.committedPaths.length,
    };
    assertBoundedToolLoadMetricFields(properties);
    captureOrganicToolLoad("tool_load_milestone", properties);
    dispatchRunnerEvent({
      kind: "milestone",
      surface,
      milestone: "route_loading_commit",
      durationMs: properties.duration_ms,
      commitCount: properties.commit_count,
    });
  }, []);

  useEffect(() => {
    if (toolLoadTrace?.state !== "active") return;
    const key = `${toolLoadTrace.traceId}:switch_start`;
    if (recordedMilestones.current.has(key)) return;
    recordedMilestones.current.add(key);
    const properties = {
      milestone: "switch_start",
      surface: toolLoadTrace.targetSurface,
      duration_ms: 0,
      commit_count: 0,
    };
    assertBoundedToolLoadMetricFields(properties);
    captureOrganicToolLoad("tool_load_milestone", properties);
    dispatchRunnerEvent({
      kind: "milestone",
      surface: toolLoadTrace.targetSurface,
      milestone: "switch_start",
      durationMs: 0,
      commitCount: 0,
      startedAtMs: toolLoadTrace.startedAtMs,
    });
  }, [toolLoadTrace]);

  useEffect(() => {
    if (toolLoadTrace?.state !== "terminal" || toolLoadTrace.terminal.kind !== "expired") return;
    const key = `${toolLoadTrace.traceId}:expired`;
    if (recordedMilestones.current.has(key)) return;
    recordedMilestones.current.add(key);
    const properties = {
      milestone: "terminal",
      surface: toolLoadTrace.targetSurface,
      terminal_kind: "expired",
      eligible: false,
      exclusion_reason: "expired",
      navigation_kind: "soft",
      viewport_class: viewportClass(),
      duration_ms: Math.max(0, toolLoadTrace.terminal.atMs - toolLoadTrace.startedAtMs),
      commit_count: toolLoadTrace.committedPaths.length,
      redirected: wasRedirected(toolLoadTrace),
    };
    assertBoundedToolLoadMetricFields(properties);
    captureOrganicToolLoad("tool_load_terminal", properties);
    dispatchRunnerEvent({
      kind: "terminal",
      surface: toolLoadTrace.targetSurface,
      terminalKind: "expired",
      contentSource: null,
      eligible: false,
      exclusionReason: "expired",
      visibilityState: visibilityCompromised.current || document.visibilityState !== "visible"
        ? "hidden"
        : "visible",
      navigationKind: "soft",
      nocache: false,
      targetRoute: toolLoadTrace.initialTargetPath,
      finalRoute: toolLoadTrace.committedPaths.at(-1) ?? toolLoadTrace.initialTargetPath,
      routeChain: [...toolLoadTrace.committedPaths],
      durationMs: properties.duration_ms,
      commitCount: properties.commit_count,
      redirected: properties.redirected,
      paintMethod: null,
      viewport: viewportClass(),
    });
  }, [toolLoadTrace]);

  useEffect(() => {
    if (toolLoadTrace?.state !== "active") return;
    document.querySelectorAll("[data-tool-loading-surface]").forEach(recordLoadingCommit);
    const mutations = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches("[data-tool-loading-surface]")) recordLoadingCommit(node);
          node.querySelectorAll("[data-tool-loading-surface]").forEach(recordLoadingCommit);
        }
      }
    });
    mutations.observe(document.body, { childList: true, subtree: true });
    return () => mutations.disconnect();
  }, [recordLoadingCommit, toolLoadTrace?.state, toolLoadTrace?.traceId]);

  useLayoutEffect(() => {
    const onFreshValidation = (event: Event) => {
      const candidate = (event as CustomEvent<FreshValidationCandidate>).detail;
      const trace = traceRef.current;
      if (
        !trace ||
        candidate?.surface !== trace.targetSurface ||
        !["client-200", "client-304", "delta-valid", "semantic-empty"].includes(candidate?.source) ||
        !Number.isFinite(candidate?.completedAtMs)
      ) return;
      if (trace.state === "active") {
        const pending = pendingFreshValidation.current;
        if (
          !pending ||
          pending.traceId !== trace.traceId ||
          candidate.completedAtMs < pending.candidate.completedAtMs
        ) {
          pendingFreshValidation.current = { traceId: trace.traceId, candidate };
        }
        return;
      }
      const pending = pendingFreshValidation.current;
      if (pending?.traceId === trace.traceId) {
        pendingFreshValidation.current = null;
        recordFreshValidation(
          trace,
          pending.candidate.completedAtMs <= candidate.completedAtMs
            ? pending.candidate
            : candidate,
        );
        return;
      }
      recordFreshValidation(trace, candidate);
    };
    const shouldListen = toolLoadTrace?.state === "active" || (
      toolLoadTrace?.state === "terminal" &&
      (toolLoadTrace.terminal.contentSource === "snapshot-fresh" ||
        toolLoadTrace.terminal.contentSource === "degraded-usable")
    );
    if (!shouldListen) return;
    window.addEventListener(FRESH_VALIDATION_EVENT, onFreshValidation);
    return () => window.removeEventListener(FRESH_VALIDATION_EVENT, onFreshValidation);
  }, [
    recordFreshValidation,
    toolLoadTrace?.state,
    toolLoadTrace?.traceId,
    toolLoadTrace?.state === "terminal" ? toolLoadTrace.terminal.contentSource : null,
  ]);

  useLayoutEffect(() => {
    if (toolLoadTrace?.state !== "terminal") return;
    const pending = pendingFreshValidation.current;
    if (!pending || pending.traceId !== toolLoadTrace.traceId) return;
    pendingFreshValidation.current = null;
    recordFreshValidation(toolLoadTrace, pending.candidate);
  }, [recordFreshValidation, toolLoadTrace?.state, toolLoadTrace?.traceId]);

  useLayoutEffect(() => {
    if (toolLoadTrace?.state !== "active") return;
    const recordPaint = (candidate: PaintCandidate) => {
      const registration = registeredContent?.get(candidate.contentId);
      if (!registration || !isVisibleContent(registration.element)) return;
      const traceAtPaint = traceRef.current;
      if (
        traceAtPaint?.state !== "active" ||
        traceAtPaint.targetSurface !== registration.receipt.surface ||
        candidate.paintedAtMs < Math.max(traceAtPaint.startedAtMs, registration.committedAtMs)
      ) return;
      const rowBearing = registration.hasRealRow;
      if (
        rowBearing &&
        traceAtPaint.state === "active"
      ) {
        const key = `${traceAtPaint.traceId}:first_real_row`;
        if (!recordedMilestones.current.has(key)) {
          recordedMilestones.current.add(key);
          dispatchRunnerEvent({
            kind: "milestone",
            surface: registration.receipt.surface,
            milestone: "first_real_row",
            durationMs: Math.max(0, candidate.paintedAtMs - traceAtPaint.startedAtMs),
            commitCount: traceAtPaint.committedPaths.length,
            contentSource: registration.receipt.source,
            paintMethod: candidate.method,
          });
        }
      }
      const finished = completeToolLoad(registration.receipt, candidate.paintedAtMs);
      if (!finished) return;

      const primary = classifyPrimarySample({
        visibilityState: visibilityCompromised.current ? "hidden" : document.visibilityState,
        hardNavigation: false,
        nocache: registration.receipt.nocache,
        terminal: finished.terminal,
      });
      const properties = {
        milestone: registration.receipt.trustworthy
          ? "first_trustworthy_content_paint"
          : "terminal_paint",
        surface: registration.receipt.surface,
        terminal_kind: finished.terminal.kind,
        ...(registration.receipt.source
          ? { content_source: registration.receipt.source }
          : {}),
        eligible: primary.eligible,
        ...(!primary.eligible ? { exclusion_reason: primary.reason } : {}),
        navigation_kind: "soft",
        viewport_class: viewportClass(),
        duration_ms: Math.max(0, candidate.paintedAtMs - finished.startedAtMs),
        commit_count: finished.committedPaths.length,
        redirected: wasRedirected(finished),
        paint_method: candidate.method,
      };
      assertBoundedToolLoadMetricFields(properties);
      captureOrganicToolLoad("tool_load_terminal", properties);
      dispatchRunnerEvent({
        kind: "terminal",
        surface: registration.receipt.surface,
        terminalKind: finished.terminal.kind,
        contentSource: registration.receipt.source ?? null,
        eligible: primary.eligible,
        exclusionReason: primary.eligible ? null : primary.reason,
        visibilityState: visibilityCompromised.current || document.visibilityState !== "visible"
          ? "hidden"
          : "visible",
        navigationKind: "soft",
        nocache: registration.receipt.nocache,
        targetRoute: finished.initialTargetPath,
        finalRoute: finished.committedPaths.at(-1) ?? finished.initialTargetPath,
        routeChain: [...finished.committedPaths],
        durationMs: properties.duration_ms,
        commitCount: properties.commit_count,
        redirected: properties.redirected,
        paintMethod: candidate.method,
        viewport: properties.viewport_class,
      });
    };

    const onFallback = (event: Event) => {
      recordPaint((event as CustomEvent<PaintCandidate>).detail);
    };
    window.addEventListener(PAINT_CANDIDATE_EVENT, onFallback);

    let observer: PerformanceObserver | null = null;
    if (typeof PerformanceObserver !== "undefined") {
      try {
        observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const identifier = (entry as PerformanceEntry & { identifier?: string }).identifier;
            if (!identifier) continue;
            recordPaint({
              contentId: identifier,
              method: "element-timing",
              paintedAtMs: entry.startTime,
            });
          }
        });
        observer.observe({ type: "element", buffered: true });
      } catch {
        observer?.disconnect();
        observer = null;
      }
    }

    return () => {
      window.removeEventListener(PAINT_CANDIDATE_EVENT, onFallback);
      observer?.disconnect();
    };
  }, [completeToolLoad, toolLoadTrace?.state, toolLoadTrace?.traceId]);

  return null;
}
